import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteBusinessUnitOverride,
  deliveryPartnerBusinessUnitOverrideSelector,
  FcrBusinessUnitSelector,
  fetchBusinessUnitOverrides,
  resetUpdateOverridesStatus,
} from "../../../../redux/features/deliveryPartnerSlice";
import { Button, Col, Modal, Row, ModalBody } from "reactstrap";
import I18n from "../../../../../i18n/utils";
import IBusinessUnitOverrides from "../../../../interfaces/IBusinessUnitOverrides";
import {
  convertMillisecondsToStringDate,
  formatReverseDateFullYear,
  formatShortDate,
} from "../../../../utils/DateUtil";
import DataTable, { IDataTableColumn } from "react-data-table-component";
import CFASpinner from "../../../Common/CFASpinnner/CFASpinner";
import assess from "../../../../assets/icons/assess.svg";
import successIcon from "../../../../assets/icons/success-icon.svg";
import DeliveryPartnerBusinessUnitOverride from "../../../../interfaces/DeliveryPartnerBusinessUnitOverride";
import XSLX from "xlsx";
import BusinessUnitOverrideForm from "../BusinessPartnersForm/BusinessUnitOverrideForm";

const BusinessUnitOverridesTable: React.FC<IBusinessUnitOverrides> = ({
  deliveryPartnerId,
  deliveryPartnerName,
  deliveryPartnerBusinessUnitOverrides,
}: IBusinessUnitOverrides): JSX.Element => {
  const [displayUpdateModal, setDisplayUpdateModal] = useState(false);
  const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
  const [displayDeleteOverridesModal, setDisplayDeleteOverridesModal] =
    useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedCell, setSelectedCell] = useState({});
  const dispatch = useDispatch();
  const businessUnits = useSelector(FcrBusinessUnitSelector);

  const businessUnitOverridesForPartner = useSelector(
    deliveryPartnerBusinessUnitOverrideSelector
  );

  // TODO implement state management for the following
  let loading = false;

  // TODO implement error handling
  // useEffect(() => {
  //   async function checkErrorOnDelete() {
  //     if (props.deleteError) {
  //       await setDeleteError(true);
  //       const deleteMessage = props.deleteMessage;
  //       await setErrorMessage(deleteMessage.error)
  //     }
  //   }
  //
  //   checkErrorOnDelete();
  // }, [props.deleteError, props.deleteMessage, props.loading]);

  const toggleDeleteOverridesModal = () =>
    setDisplayDeleteOverridesModal(!displayDeleteOverridesModal);

  const exportAndDeleteOverridesForPartner = () => {
    exportBusinessUnitOverrides();
    deleteAllOverridesForPartner();
  };

  const deleteOverridesForPartner = () => {
    exportBusinessUnitOverrides();
    deleteAllOverridesForPartner();
  };

  // remove overrides for the currently selected partner
  const deleteAllOverridesForPartner = async () => {
    const updatedOverrides = deliveryPartnerBusinessUnitOverrides.filter(
      (override) => {
        return override.deliveryPartnerId === deliveryPartnerId;
      }
    );

    for (let i = 0; i < updatedOverrides.length; i++) {
      // @ts-ignore
      dispatch(
        deleteBusinessUnitOverride(
          //@ts-ignore
          updatedOverrides[i].deliveryPartnerBusinessUnitOverrideId
        )
      );
    }
    showOnDeleteSuccessModal();
  };

  const showDeleteOverridesModal = () => {
    setDisplayDeleteOverridesModal(true);
  };

  const showUpdateModal = (cell: any) => {
    dispatch(resetUpdateOverridesStatus());
    // When adding an override, ensure deliveryPartnerId is defined
    if (!cell.deliveryPartnerId) {
      cell["deliveryPartnerId"] = deliveryPartnerId;
    }
    setSelectedCell(cell);
    setDisplayUpdateModal(true);
  };

  const showOnDeleteSuccessModal = () => {
    if (deleteError) {
      alert(errorMessage);
    } else {
      setDisplayDeleteModal(true);
      dispatch(fetchBusinessUnitOverrides());
    }
  };

  const closeUpdateModal = () => {
    dispatch(resetUpdateOverridesStatus());
    setSelectedCell({});
    setDisplayUpdateModal(false);
    // Clear form
    //props.resetFormModal();
    // Get all Overrides again
    // @ts-ignore
    dispatch(fetchBusinessUnitOverrides(deliveryPartnerId));
  };

  const closeDeleteModal = () => {
    setDisplayDeleteModal(false);
    // @ts-ignore
    dispatch(fetchBusinessUnitOverrides(deliveryPartnerId));
  };

  const exportBusinessUnitOverrides = () => {
    let ws = XSLX.utils.json_to_sheet(
      deliveryPartnerBusinessUnitOverrides.map((bu) => {
        return {
          "Store #": bu.locationNumber,
          "Commission Rate (%)": bu.commissionRate,
          "Commission Fee ($)": bu.overrideCommissionFee,
          "Effective Date": formatReverseDateFullYear(bu.startTimestamp),
          "Rollback Date": bu.endTimestamp
            ? formatReverseDateFullYear(bu.endTimestamp)
            : null,
          "Override ID": bu.deliveryPartnerBusinessUnitOverrideId,
          "Digital Channel ID": bu.deliveryPartnerId,
          lastUpdatedBy: bu.lastUpdatedBy,
          lastUpdatedTimestamp: convertMillisecondsToStringDate(
            bu.lastUpdatedTimestamp
          ),
          createdBy: bu.createdBy,
          creationTimestamp: convertMillisecondsToStringDate(
            bu.creationTimestamp
          ),
          objectVersionNumber: bu.objectVersionNumber,
        };
      })
    );

    let wb = XSLX.utils.book_new();
    XSLX.utils.book_append_sheet(wb, ws, "Current");
    XSLX.writeFile(wb, `${deliveryPartnerName}-Overrides.xlsx`);
  };

  const columns: IDataTableColumn<DeliveryPartnerBusinessUnitOverride>[] = [
    {
      cell: (cell: any) => (
        <img
          id="logoTooltip"
          src={assess}
          width="20"
          height="20"
          className="edit-icon"
          alt="CFA Logo"
          onClick={() => showUpdateModal(cell)}
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: I18n.t(
        "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_LOCATION_NUMBER_LABEL"
      ),
      selector: "locationNumber",
      sortable: true,
    },
    {
      name: I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_RATE_LABEL"),
      selector: "commissionRate",
      cell: (cell) => <h3 className="custom-cell">{cell.commissionRate}%</h3>,
      sortable: true,
    },
    {
      name: I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_COMMISSION_FEE_LABEL"),
      selector: "overrideCommissionFee",
      cell: (cell) => (
        <h3 className="custom-cell">
          {cell.overrideCommissionFee
            ? "$" + cell.overrideCommissionFee
            : "$0.00"}
        </h3>
      ),
      sortable: true,
    },
    {
      name: I18n.t(
        "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_START_TIMESTAMP_LABEL"
      ),
      selector: "startTimestamp",
      cell: (cell) => (
        <h3 className="custom-cell">
          {formatShortDate(cell.startTimestamp.toString())}
        </h3>
      ),
      sortable: true,
    },
    {
      name: I18n.t(
        "APP_BUSINESS_UNIT_OVERRIDES_UPDATE_FORM_END_TIMESTAMP_LABEL"
      ),
      selector: "endTimestamp",

      cell: (cell) => (
        <h3 className="custom-cell">
          {cell.endTimestamp != null
            ? formatShortDate(cell.endTimestamp.toString())
            : I18n.t("APP_BUSINESS_UNIT_OVERRIDES_UPDATE_NOT_APPLICABLE")}
        </h3>
      ),
      sortable: true,
    },
  ];

  const actions = (
    <>
      <Button className="add-override-modal-button" onClick={showUpdateModal}>
        {I18n.t("APP_BUSINESS_UNIT_OVERRIDES_ADD_FORM_TITLE")}
      </Button>
      {businessUnitOverridesForPartner.length !== 0 && (
        <Button
          className="add-override-modal-button"
          onClick={showDeleteOverridesModal}
        >
          {I18n.t("APP_BUSINESS_UNIT_OVERRIDES_DELETE_ALL_FOR_PARTNER_BUTTON")}
        </Button>
      )}
      <Button
        className="add-override-modal-button"
        onClick={exportBusinessUnitOverrides}
      >
        {I18n.t("APP_BUSINESS_UNIT_OVERRIDES_EXPORT")}
      </Button>
    </>
  );

  return (
    <div className="BusinessUnitOverridesTable">
      {!loading ? (
        <DataTable
          title={I18n.t("APP_BUSINESS_UNIT_OVERRIDES_TITLE")}
          actions={actions}
          keyField="deliveryPartnerBusinessUnitOverrideId"
          defaultSortField="locationNumber"
          defaultSortAsc={true}
          columns={columns}
          data={businessUnitOverridesForPartner}
          pagination
          persistTableHead
          responsive
          dense
          striped
        />
      ) : (
        <CFASpinner />
      )}

      {/* Update/Edit Form Modal */}
      <Modal
        isOpen={displayUpdateModal}
        className="modal-dialog"
        modalClassName="add-partner-modal"
      >
        <BusinessUnitOverrideForm
          //@ts-ignore
          selectedOverride={selectedCell}
          close={closeUpdateModal}
          businessUnits={businessUnits}
        />
      </Modal>

      <Modal
        isOpen={displayDeleteOverridesModal}
        className="delete-overrides-modal"
        centered={true}
      >
        <ModalBody>
          <h1>
            {I18n.t("APP_BUSINESS_UNIT_OVERRIDES_DELETE_ALL_FOR_PARTNER_TITLE")}
          </h1>
          <h6>
            {I18n.t(
              "APP_BUSINESS_UNIT_OVERRIDES_DELETE_ALL_FOR_PARTNER_MESSAGE"
            )}
          </h6>

          <Row>
            <Col className="delete-overrides-container">
              <Button
                className="export-delete-button"
                block
                onClick={exportAndDeleteOverridesForPartner}
              >
                {I18n.t("APP_BUSINESS_UNIT_OVERRIDES_EXPORT_DELETE_LABEL")}
              </Button>
            </Col>
            <Col className="delete-overrides-container">
              <Button
                className="delete-button"
                block
                onClick={deleteOverridesForPartner}
              >
                {I18n.t("APP_BUSINESS_UNIT_OVERRIDES_DELETE_LABEL")}
              </Button>
            </Col>
            <Col className="delete-overrides-container">
              <Button
                className="cancel-button"
                block
                onClick={toggleDeleteOverridesModal}
              >
                {I18n.t("APP_BUSINESS_UNIT_OVERRIDES_CANCEL_LABEL")}
              </Button>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* Success on Delete All Modal */}
      <Modal
        isOpen={displayDeleteModal}
        className="modal-dialog"
        modalClassName="add-partner-modal"
      >
        {deleteError ? (
          <Row>
            <Col className="success-container">
              <h4>{I18n.t("APP_DELIVERY_PARTNERS_DELETE_FAILURE")}</h4>
              <p>Error Message: {errorMessage}</p>
              <Button
                className="go-back-button"
                block
                onClick={closeDeleteModal}
              >
                {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_CLOSE")}
              </Button>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col className="success-container">
              <img alt="" src={successIcon} className="success-icon" />
              <h4>{I18n.t("APP_DELIVERY_PARTNERS_DELETE_SUCCESS")}</h4>
              <Button
                className="go-back-button"
                block
                onClick={closeDeleteModal}
              >
                {I18n.t("APP_DELIVERY_PARTNERS_UPDATE_FORM_CLOSE")}
              </Button>
            </Col>
          </Row>
        )}
      </Modal>
    </div>
  );
};

export default BusinessUnitOverridesTable;
